import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { autoOrderAPI } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Lightning, 
  ShoppingCart, 
  Warning, 
  Package, 
  CaretDown,
  CaretRight,
  Truck,
  Check
} from '@phosphor-icons/react';

export default function AutoOrderSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [creatingOrder, setCreatingOrder] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await autoOrderAPI.getSuggestions();
      setSuggestions(res.data);
      // Expand all by default
      const expanded = {};
      res.data.forEach(s => {
        expanded[s.supplier_id || 'no-supplier'] = true;
      });
      setExpandedSuppliers(expanded);
    } catch (error) {
      toast.error('Öneriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const toggleSupplier = (supplierId) => {
    setExpandedSuppliers({
      ...expandedSuppliers,
      [supplierId || 'no-supplier']: !expandedSuppliers[supplierId || 'no-supplier']
    });
  };

  const handleCreateOrder = async (supplierId) => {
    if (!supplierId) {
      toast.error('Tedarikçisi olmayan ürünler için sipariş oluşturulamaz');
      return;
    }
    
    setCreatingOrder(supplierId);
    try {
      const res = await autoOrderAPI.createOrder(supplierId);
      toast.success(`Sipariş oluşturuldu! ${res.data.total_items} ürün, toplam ${formatCurrency(res.data.total_amount)}`);
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sipariş oluşturulamadı');
    } finally {
      setCreatingOrder(null);
    }
  };

  const totalItems = suggestions.reduce((sum, s) => sum + s.total_items, 0);
  const totalCost = suggestions.reduce((sum, s) => sum + s.total_estimated_cost, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Otomatik Sipariş Önerisi
            </h1>
            <p className="mt-2 text-muted-foreground">
              Düşük stoklu ürünler için tedarikçi bazlı sipariş önerileri
            </p>
          </div>
          <Button variant="outline" onClick={fetchSuggestions}>
            Yenile
          </Button>
        </div>

        {/* Summary */}
        {suggestions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-border bg-card p-5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <Warning size={24} weight="duotone" className="text-destructive" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Düşük Stoklu Ürün</p>
                  <p className="text-2xl font-heading font-bold">{totalItems}</p>
                </div>
              </div>
            </div>
            <div className="border border-border bg-card p-5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Truck size={24} weight="duotone" className="text-primary" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Tedarikçi Sayısı</p>
                  <p className="text-2xl font-heading font-bold">{suggestions.length}</p>
                </div>
              </div>
            </div>
            <div className="border border-border bg-card p-5 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <ShoppingCart size={24} weight="duotone" className="text-secondary" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Tahmini Toplam</p>
                  <p className="text-2xl font-heading font-bold font-data">{formatCurrency(totalCost)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suggestions by Supplier */}
        {suggestions.length === 0 ? (
          <div className="border border-border bg-card p-12 rounded-xl text-center">
            <Check size={48} weight="duotone" className="mx-auto text-secondary mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">Tebrikler!</h3>
            <p className="text-muted-foreground">Tüm ürünlerinizin stoku yeterli seviyede.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion) => {
              const key = suggestion.supplier_id || 'no-supplier';
              const isExpanded = expandedSuppliers[key];
              const isNoSupplier = !suggestion.supplier_id;
              
              return (
                <div key={key} className="border border-border bg-card rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSupplier(suggestion.supplier_id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`supplier-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                      <div className="text-left">
                        <h3 className="font-heading font-semibold">
                          {suggestion.supplier_name}
                          {isNoSupplier && (
                            <span className="ml-2 text-xs text-destructive">(Tedarikçi Ata!)</span>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {suggestion.total_items} ürün • Tahmini: {formatCurrency(suggestion.total_estimated_cost)}
                        </p>
                      </div>
                    </div>
                    {!isNoSupplier && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateOrder(suggestion.supplier_id);
                        }}
                        disabled={creatingOrder === suggestion.supplier_id}
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`create-order-${key}`}
                      >
                        <Lightning size={18} className="mr-2" />
                        {creatingOrder === suggestion.supplier_id ? 'Oluşturuluyor...' : 'Sipariş Oluştur'}
                      </Button>
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="border-t border-border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead>Ürün</TableHead>
                            <TableHead>Kategori</TableHead>
                            <TableHead className="text-right">Mevcut</TableHead>
                            <TableHead className="text-right">Minimum</TableHead>
                            <TableHead className="text-right">Eksik</TableHead>
                            <TableHead className="text-right">Önerilen</TableHead>
                            <TableHead className="text-right">Birim Fiyat</TableHead>
                            <TableHead className="text-right">Tahmini Maliyet</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {suggestion.items.map((item) => (
                            <TableRow key={item.product_id} className="border-b border-border/40">
                              <TableCell className="font-medium">{item.product_name}</TableCell>
                              <TableCell className="text-muted-foreground">{item.category}</TableCell>
                              <TableCell className="text-right">
                                <span className={item.current_stock === 0 ? 'text-destructive font-semibold' : ''}>
                                  {formatNumber(item.current_stock)} {item.unit}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-data text-muted-foreground">
                                {formatNumber(item.min_stock_level)} {item.unit}
                              </TableCell>
                              <TableCell className="text-right font-data text-destructive">
                                -{formatNumber(item.shortage)} {item.unit}
                              </TableCell>
                              <TableCell className="text-right font-data font-semibold text-secondary">
                                {formatNumber(item.suggested_quantity)} {item.unit}
                              </TableCell>
                              <TableCell className="text-right font-data">
                                {formatCurrency(item.unit_price)}
                              </TableCell>
                              <TableCell className="text-right font-data font-semibold">
                                {formatCurrency(item.estimated_cost)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="border border-accent/30 bg-accent/10 p-6 rounded-xl">
          <div className="flex items-start gap-3">
            <Lightning size={24} weight="duotone" className="text-accent-foreground mt-1" />
            <div>
              <h3 className="font-heading font-semibold mb-2">Otomatik Sipariş Nasıl Çalışır?</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Stoku minimum seviyenin altına düşen ürünler otomatik tespit edilir</li>
                <li>• Önerilen miktar, stoku güvenli seviyeye çıkaracak şekilde hesaplanır (min. 2x)</li>
                <li>• Ürünlere tedarikçi atayarak grup siparişler oluşturabilirsiniz</li>
                <li>• "Sipariş Oluştur" ile tek tıkla satın alma siparişi açılır</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
